import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, inject } from '@angular/core';

import { Fai, FaimUnlimited, Plan, ProductFactory, Subscription } from '@bytel/bytel-sales';
import {
    CheckoutStorageService,
    CommercialProposal,
    CurrentFundingMethods,
    DeliveryService,
    FUNDING_ENUM,
    FundingCart,
    FundingMethod,
    FundingModeReturn,
    FundingRepository,
    FundingService,
    PaymentMode,
    PostOrderResponse,
    PostReceiptResponse,
    ScoringService,
    UserService,
} from '@bytel/checkout';

import { SHIPPING_MODES } from '@base/common/constants/index';
import { ChatbotService } from '@base/common/services/chatbot.service';

import { ApiService } from '@services/api.service';
import { AppService } from '@services/app.services';
import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';

import { ByouErrorModalComponent } from '@shared/byou-error-modal/byou-error-modal.component';
import { ErrorModalComponent } from '@shared/error-modal/error-modal.component';

import * as Fingerprint2 from 'fingerprintjs2';
import { Observable, forkJoin, from, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, retry } from 'rxjs/operators';

import { UtilsHelper } from '../helper/utils-helper';
import { BankingVerificationApiPayload, OpenBankingStatus } from '../interfaces/api/open-banking.api.interface';
import { BasicObject, ObjectOfString } from '../interfaces/checkout.interface';
import { ScoringV3API } from '../interfaces/scoring-api.interface';
import { Action, ScoringAction, ScoringScope, ScoringType, ScoringV3 } from '../interfaces/scoring.interface';
import { CheckoutOauth2ResourcesService } from '../services/oauth2-ressources.service';
import { PaymentRepository } from './../repositories/payment.repository';
import { ScoringModalComponent } from './../tunnel/payment/components/scoring-modal/scoring-modal.component';
import { ConfigurationService } from './configuration.service';

@Injectable({
    providedIn: 'root',
})
export class PaymentService {
    public static readonly FUNDING_DEFAULT_KEY = 'funding_key';
    public readonly REDIRECT_PARAMS = {
        key: 'redirect',
        value: 'true',
    };
    public readonly NO_PAYMENT: string = 'SANS_PAIEMENT';
    public fundingCashMethod: FundingMethod = null;

    private userService: UserService = inject(UserService);
    private configService: ConfigurationService = inject(ConfigurationService);

    public constructor(
        // NOSONAR
        protected cartService: MainCartService,
        protected oauth2Resource: CheckoutOauth2ResourcesService,
        protected checkoutStorage: CheckoutStorageService,
        protected paymentRepository: PaymentRepository,
        @Inject(DOCUMENT) private document: Document,
        protected scoringService: ScoringService,
        protected appService: AppService,
        protected messageService: MessageService,
        protected chatBotService: ChatbotService,
        protected apiService: ApiService,
        protected deliveryService: DeliveryService,
        protected fundingRepository: FundingRepository,
        protected fundingService: FundingService,
    ) {}

    public checkIban(params: ObjectOfString): Observable<BasicObject> {
        const checkIbanObs: Observable<BasicObject>[] = [];
        this.cartService.cartModel.quotes.forEach((quote) => {
            checkIbanObs.push(this.paymentRepository.checkIban(params, this.cartService.cartModel.cartId, quote.id));
        });
        return forkJoin(checkIbanObs);
    }

    /**
     * Get scoring data for current cart
     * @returns Scoring details
     */
    public getScoring(): Observable<ScoringV3> {
        const hasCreditIntention = this.cartService.cartModel.fundingMethod?.type === FUNDING_ENUM.YOUNITED;
        return this.paymentRepository.getScoringV3(this.cartService.cartModel.cartId, hasCreditIntention).pipe(
            map(
                (data: ScoringV3API) => {
                    const scoring: ScoringV3 = {
                        id: data.id,
                        results: data.resultats?.map((result) => ({
                            id: result.id,
                            type: result.type,
                            applicationScope: {
                                type: result.champApplication.type as ScoringScope,
                                businessLine: result.champApplication.ligneMarcheMetier,
                                category: result.champApplication.categorie,
                            },
                            result: result.resultat,
                            perimeter: result.perimetre,
                            reliability: result.fiabilite,
                            actions: result.actions?.map((a) => {
                                const action: Action = {
                                    type: a.action as ScoringAction,
                                    amount: a.montant,
                                };
                                return action;
                            }),
                            message: result.message,
                            impactedSalesQuotes: result.parcoursVenteImpactes?.map((quote) => ({
                                idSalesQuote: quote.idParcoursVente,
                            })),
                            unlockingFeature: {
                                unlockable: result.caracteristiqueDeblocage.deblocable,
                            },
                        })),
                    };
                    return scoring;
                },
                catchError((error) => {
                    console.log(error);
                    return of(null);
                }),
            ),
        );
    }

    public getFundingCarts(): Observable<FundingCart> {
        return this.fundingRepository.getFundingCart(this.cartService.cartModel.cartId);
    }

    public getCurrentFundingMethod(): Observable<CurrentFundingMethods> {
        return this.fundingRepository.getCurrentFundingMethod(this.cartService.cartModel.cartId);
    }

    public getStoredFingerPrint(): string {
        return this.checkoutStorage.get('deviceFingerPrint', false);
    }

    public getUniqueUserId(): string {
        let uuid: string = this.checkoutStorage.get('UUID', false);

        if (!uuid) {
            const timestamp = new Date().getTime().toString().split('');
            const hash: string[] = [];

            for (const ts of timestamp) {
                hash.push(
                    `${Math.floor(1 + UtilsHelper.GetRandomValue() * 0x10000)
                        .toString(16)
                        .substring(1)}${ts}`,
                );
            }
            uuid = hash.join('');
            this.checkoutStorage.set('UUID', uuid, false);
        }

        return uuid;
    }

    public getFingerPrint(keys): string {
        const unallowedKeys: string[] = ['canvas', 'webgl', 'regular_plugins'];
        let indexCount: number = 0;

        for (const key of keys) {
            for (const index of unallowedKeys) {
                const unallowedKey: string = unallowedKeys.find((el) => index === el);
                if (key.key === unallowedKey) {
                    const forbiddenKeyIndex: number = keys.findIndex((el) => el.key === index);
                    keys.splice(forbiddenKeyIndex, 1);
                    indexCount++;
                    if (indexCount === unallowedKeys.length) {
                        break;
                    }
                }
            }
        }
        const values: any[] = keys.map((component) => component.value);

        const fingerPrint: string = Fingerprint2.x64hash128(values.join('~~~'), 31);
        this.checkoutStorage.set('deviceFingerPrint', fingerPrint, false);
        this.checkoutStorage.set('deviceFingerPrintKeys', keys, false);

        return fingerPrint;
    }

    public postFundingMode(fundingCart: FundingCart, paymentMode: PaymentMode): Observable<FundingModeReturn> {
        const cartModel = this.cartService.cartModel;
        let proposal: CommercialProposal = null;
        if (cartModel.fundingMethod?.type === FUNDING_ENUM.YOUNITED) {
            proposal = this.fundingService.getYounitedSelectedProposal(cartModel.fundingMethod);
        }

        if (cartModel.fundingMethod?.type === FUNDING_ENUM.COFIDIS) {
            proposal = this.fundingService.getCofidisSelectedProposal(cartModel.fundingMethod);
        }

        return this.fundingRepository.postFundingMode(this.cartService.cartModel, fundingCart, paymentMode, proposal);
    }

    public handleFundingModeSucess(
        isSowo: boolean,
        cartId = this.cartService.cartModel.cartId,
    ): Observable<PostReceiptResponse> {
        if (!cartId) {
            throw new Error('Cart ID is missing');
        }

        const params = this._handleParams();
        params.cartId = cartId;

        if (params?.fingerprint) {
            return this._createOrder(params, isSowo);
        }

        return from(Fingerprint2.getPromise()).pipe(
            mergeMap((keys) => {
                params.fingerprint = this.getFingerPrint(keys);
                return this._createOrder(params, isSowo);
            }),
        );
    }

    /**
     * Get scoring
     * @returns boolean
     */
    public scoring(): Observable<boolean> {
        return this.getScoring().pipe(
            catchError(() => of(null)),
            mergeMap((data: ScoringV3) => {
                if (data) {
                    const scoringError = data.results.find(
                        (result) =>
                            result.type === ScoringType.GLOBAL_RESULT &&
                            (result.result === 'KO' || result.result === 'KO_SAUF_ACTION_REALISEE') &&
                            result.applicationScope.type === 'ParActeDemande',
                    );
                    if (scoringError) {
                        this.messageService.openModal(ScoringModalComponent, {
                            scoringResults: data.results.filter(
                                (result) =>
                                    result.type === ScoringType.GLOBAL_RESULT &&
                                    (result.result === 'KO' || result.result === 'KO_SAUF_ACTION_REALISEE') &&
                                    (result.applicationScope.type === ScoringScope.MARKET_LINE ||
                                        result.applicationScope.type === ScoringScope.REQUESTED_ACT),
                            ),
                        });
                        return of(false);
                    }
                }
                return of(true);
            }),
        );
    }

    /**
     * Score user
     * If open banking need
     * 1. Check if user has already done a jounery and return IBAN
     * 2. If not, ask for open banking verification
     * @returns
     */
    public shouldUseOpenBanking(): Observable<{ id?: string; url?: string; iban?: string } | undefined> {
        const hasOpenbanking = this.checkoutStorage.get('hasOpenBanking');

        const isFam =
            this.cartService.getQuote(0)?.getProductByType<Plan>('Plan')?.data?.commercial_range ===
                'forfait_avec_mobile' || false;
        if (!this.configService.getData().global.features.openBankingEnabled || !isFam) {
            return of(undefined);
        }
        return this.getScoring().pipe(
            catchError(() => of(null)),
            mergeMap((data: ScoringV3) => {
                if (data) {
                    return of(this._handleScoringData(data));
                }

                return of(false);
            }),
            mergeMap((checkOb) => {
                if (checkOb || hasOpenbanking) {
                    return this.getOpenBankingStatus().pipe(
                        mergeMap((data) => {
                            if (data?.iban) {
                                return of({
                                    id: data.id,
                                    url: undefined,
                                    iban: data.iban,
                                });
                            }

                            return this.checkBankVerification().pipe(map((url) => url));
                        }),
                    );
                }
                return of(undefined);
            }),
        );
    }

    public hasSimActivation(): boolean {
        const quote = this.cartService.getQuote();
        const plan = quote.getPrincipalProduct<Subscription>('Subscription');
        const hasPortability: boolean = !!quote?.forms?.portability?.active;
        const deliveryInfos = this.cartService.getDelivery();
        const isCCollectMode: boolean = deliveryInfos?.pickup?.deliveryType === SHIPPING_MODES.CLICK_AND_COLLECT;
        return (
            !quote.isRenewal() &&
            !isCCollectMode &&
            !hasPortability &&
            !!(plan && !ProductFactory.Is(plan, FaimUnlimited) && !ProductFactory.Is(plan, Fai))
        );
    }

    public checkBankVerification(): Observable<{ id: string; url: string; hasFailure: boolean } | null> {
        const user = this.userService.user;
        if (!user) {
            throw new Error('Missing user');
        }

        const payload: BankingVerificationApiPayload = {
            demande: {
                noPersonne: user.id,
                urlCallBack: `${this.document.location.origin}/tunnel/banque`,
            },
        };

        try {
            // TODO: stocker l'id open banking pour réuse
            return this.paymentRepository.askForBankingVerification(payload).pipe(
                map((data) => ({ id: data.id, url: data.urlParcours, hasFailure: false })),
                catchError(() => of(null)),
            );
        } catch (error) {
            console.log(error);
            return of(null);
        }
    }

    public getOpenBankingStatus(): Observable<{ id: string; iban: string; statut: OpenBankingStatus }> {
        const user = this.userService.user;
        if (!user.id) {
            throw new Error('Missing user');
        }
        return this.paymentRepository.getBankingVerification(user.id).pipe(
            mergeMap((data) => {
                if (data.statut === OpenBankingStatus.REALISE) {
                    return of({ id: data.id, iban: data.informationsSupplementaires.iban, statut: data.statut });
                } else {
                    return of({ id: data.id, iban: undefined, statut: data.statut });
                }
            }),
            retry(3),
            catchError(() => of(null)),
        );
    }

    public getModalPaymentTitle(fundingType: string, proposal: CommercialProposal): string {
        let title: string = 'Paiement en';
        if (fundingType === FUNDING_ENUM.COFIDIS) {
            title += ` ${proposal.nbLoan + 1} fois`;
        } else {
            title += ` ${proposal.nbLoan} fois`;
        }

        if (proposal.interestAmount > 0) {
            title += ' avec frais';
        } else {
            title += ' sans frais';
        }
        return title;
    }

    private _handleScoringData(data: ScoringV3): boolean {
        if (data) {
            const scoringError = data.results.find(
                (result) =>
                    result.type === ScoringType.GLOBAL_RESULT &&
                    (result.result === 'KO' || result.result === 'KO_SAUF_ACTION_REALISEE') &&
                    result.applicationScope.type === 'ParActeDemande',
            );
            if (scoringError) {
                if (scoringError?.actions?.some((a) => a.type === ScoringAction.VERIFICATION_BANCAIRE)) {
                    return true;
                } else {
                    this.messageService.openModal(ScoringModalComponent, {
                        scoringResults: data.results.filter(
                            (result) =>
                                result.type === ScoringType.GLOBAL_RESULT &&
                                (result.result === 'KO' || result.result === 'KO_SAUF_ACTION_REALISEE') &&
                                (result.applicationScope.type === ScoringScope.MARKET_LINE ||
                                    result.applicationScope.type === ScoringScope.REQUESTED_ACT),
                        ),
                    });
                    return false;
                }
            }
        }
    }

    private _createOrder(params: ObjectOfString, isSowo: boolean): Observable<PostReceiptResponse> {
        const orderObs: Observable<PostReceiptResponse> = this.apiService.createOrder(params).pipe(
            mergeMap((paymentData: PostOrderResponse) =>
                this.chatBotService.validateOrder(paymentData?.orcomId).pipe(map(() => paymentData)),
            ),
            mergeMap((paymentData: PostOrderResponse) => {
                // Hybrid
                if (paymentData?._actions?.afficherPageMerci?.action) {
                    this.document.defaultView.location.href = paymentData?._actions?.afficherPageMerci?.action;
                    // CB
                } else if (paymentData?._actions?.PayerCommande?.action) {
                    return this._handlePaymentMode(paymentData);
                } else {
                    this._showError(isSowo);
                }
            }),
            catchError((e) => throwError(() => e)),
        );

        return this.scoring().pipe(
            mergeMap((status) => {
                if (!status) {
                    return of(null);
                }
                return orderObs;
            }),
        );
    }

    private _handlePaymentMode(paymentData: PostOrderResponse): Observable<PostReceiptResponse> {
        const paymentUrl: string = paymentData._actions.PayerCommande.action;
        const paymentParams = paymentData._actions.PayerCommande.input;
        return this.apiService.pushPaymentMode(paymentUrl, paymentParams).pipe(
            mergeMap((data) => {
                if (data?.retourPSP?.formUrl && data?.retourPSP?.method) {
                    return of(data);
                } else {
                    return of(null);
                }
            }),
        );
    }

    private _handleParams(): BasicObject {
        const uuid: string = this.getUniqueUserId();
        const storedFingerPrint: string = this.getStoredFingerPrint();
        return {
            fingerprint: storedFingerPrint,
            uuid,
        };
    }

    private _showError(isSowo: boolean): void {
        if (isSowo) {
            this.messageService.openModal(ByouErrorModalComponent);
        } else {
            this.messageService.openModal(ErrorModalComponent);
        }
    }
}
